import React, { useState, useEffect, useContext } from 'react';
import {
	faChevronLeft,
	faChevronRight,
	faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Input, Table, Card, CardBody } from "reactstrap";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";
import { PostData } from '../../../services/FetchData';
import { ProductContext } from '../../../store';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';

const ContentSearch = () => {
	console.log("ContentSearch");
	const { currentWebsiteId } = useContext(ProductContext);
	const [pageLoader, setPageLoader] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [contents, setContents] = useState([]);
	const [totalContents, setTotalContents] = useState(0);
	const [currentPage, setCurrentPage] = useState(0);
	const [itemsPerPage] = useState(20);
	const [searchTerm, setSearchTerm] = useState("");
	const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
	const [filterContentType, setFilterContentType] = useState("");
	const [filterSyncStatus, setFilterSyncStatus] = useState("");

	// Fetch content from API
	useEffect(() => {
		const fetchContent = async () => {
			const websiteId = currentWebsiteId || Cookies.get("_website_id");
			const token = Cookies.get("_cb_search_token");

			if (!websiteId) {
				console.error("No website ID found");
				toast.error("Website ID is missing. Please refresh the page.");
				setIsLoading(false);
				setPageLoader(false);
				return;
			}

			setPageLoader(true);
			try {
				const postData = {
					websiteId: websiteId,
					page: currentPage + 1,
					limit: itemsPerPage,
					token,
				};

				if (appliedSearchTerm) {
					postData.search = appliedSearchTerm;
				}
				if (filterContentType) {
					postData.contentType = filterContentType;
				}
				if (filterSyncStatus) {
					postData.syncStatus = filterSyncStatus;
				}

				const response = await PostData("ms4", "api/v1/cmsAssistant/getContentList", postData);

				if (response !== "Invalid" && response !== undefined) {
					if (response.status === "success") {
						setContents(response.data.contents || []);
						setTotalContents(response.data.pagination?.total || 0);
					} else {
						toast.error(response.message || "Failed to fetch content");
						setContents([]);
						setTotalContents(0);
					}
				} else {
					toast.error("Invalid response from server");
					setContents([]);
					setTotalContents(0);
				}
			} catch (error) {
				console.error("Error fetching content:", error);
				toast.error("An error occurred while fetching content");
				setContents([]);
				setTotalContents(0);
			} finally {
				setIsLoading(false);
				setPageLoader(false);
			}
		};

		fetchContent();
	}, [currentPage, itemsPerPage, appliedSearchTerm, filterContentType, filterSyncStatus, currentWebsiteId]);

	const handlePageClick = (data) => {
		setCurrentPage(data.selected);
	};

	const handleSearch = () => {
		setAppliedSearchTerm(searchTerm);
		setCurrentPage(0);
	};

	if (isLoading) {
		return <DetailSkeleton />;
	}

	const pageCount = Math.ceil(totalContents / itemsPerPage);

	return (
		<div className="pageContent">
			<div className="pageHeader">
				<h2 className="pageTitle">Content Search</h2>
			</div>

			<Card className="mt-4">
				<CardBody>
					<div className="d-flex gap-2 mb-3">
						<Input
							type="text"
							placeholder="Search content by title, body, author..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
							style={{ flex: 1 }}
						/>
						<Button color="primary" onClick={handleSearch}>
							<FontAwesomeIcon icon={faSearch} className="me-1" />
							Search
						</Button>
					</div>

					<div className="d-flex gap-2 mb-3">
						<Input
							type="select"
							value={filterContentType}
							onChange={(e) => {
								setFilterContentType(e.target.value);
								setCurrentPage(0);
							}}
							style={{ width: '200px' }}
						>
							<option value="">All Content Types</option>
							<option value="news_article">News Article</option>
							<option value="blog_post">Blog Post</option>
							<option value="page">Page</option>
						</Input>
						<Input
							type="select"
							value={filterSyncStatus}
							onChange={(e) => {
								setFilterSyncStatus(e.target.value);
								setCurrentPage(0);
							}}
							style={{ width: '200px' }}
						>
							<option value="">All Status</option>
							<option value="synced">Synced</option>
							<option value="ragflowUploaded">RAGFlow Uploaded</option>
							<option value="chunkFetched">Chunks Fetched</option>
							<option value="parseFailed">Parse Failed</option>
						</Input>
					</div>

					<Table responsive striped>
						<thead>
							<tr>
								<th>Title</th>
								<th>Content Type</th>
								<th>Author</th>
								<th>Status</th>
								<th>Created</th>
							</tr>
						</thead>
						<tbody>
							{pageLoader ? (
								<tr>
									<td colSpan="5">
										<DetailSkeleton />
									</td>
								</tr>
							) : (
								<>
									{contents.length > 0 ? (
										contents.map((content) => (
											<tr key={content.id}>
												<td>
													<a href={content.url} target="_blank" rel="noopener noreferrer">
														{content.title || 'Untitled'}
													</a>
												</td>
												<td>{content.contentType || '-'}</td>
												<td>{content.authorName || '-'}</td>
												<td>
													<span className={`badge ${
														content.syncStatus === 'chunkFetched' ? 'bg-success' :
														content.syncStatus === 'ragflowUploaded' ? 'bg-info' :
														content.syncStatus === 'parseFailed' ? 'bg-danger' :
														'bg-secondary'
													}`}>
														{content.syncStatus || 'Unknown'}
													</span>
												</td>
												<td>
													{content.createdAt ? format(new Date(content.createdAt), 'MMM dd, yyyy') : '-'}
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="5" className="text-center">No content found</td>
										</tr>
									)}
								</>
							)}
						</tbody>
					</Table>

					{pageCount > 1 && (
						<div className="d-flex justify-content-center mt-4">
							<ReactPaginate
								previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
								nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
								breakLabel={"..."}
								breakClassName={"break-me"}
								pageCount={pageCount}
								marginPagesDisplayed={2}
								pageRangeDisplayed={5}
								onPageChange={handlePageClick}
								containerClassName={"pagination"}
								activeClassName={"active"}
							/>
						</div>
					)}
				</CardBody>
			</Card>
		</div>
	);
};

export default ContentSearch;
